import { useMemo, useState } from "react";
import {
  GiAlarmClock,
  GiTreasureMap,
  GiShield,
  GiFairyWand,
  GiCrystalCluster,
  GiCrystalShine,
} from "react-icons/gi";
import { useCastle } from "../api/castle";
import {
  InventoryItemDef,
  InventoryItems,
  InventoryItemMap,
  InventoryGroup,
  groupOf,
  useOpenBundle,
  useApplyShield,
  useApplyBuff,
} from "../api/inventory";
import { Loading } from "../pages/Loading";
import { ErrorView } from "../pages/ErrorView";
import { hapticImpact, hapticNotification, showAlert } from "../lib/telegram";
import { formatNumber } from "../lib/format";

interface Props {
  enabled: boolean;
}

interface FilterDef {
  id: InventoryGroup;
  label: string;
}

const filters: FilterDef[] = [
  { id: "all", label: "Все" },
  { id: "speedup", label: "Ускорители" },
  { id: "bundle", label: "Сундуки" },
  { id: "shield", label: "Щиты" },
  { id: "buff", label: "Баффы" },
  { id: "hero", label: "Герои" },
];

// New inventory layout: 4-column grid of tiles, with category filter chips
// at the top. Each tile shows icon + name + quantity badge. Tap → detail
// modal with description + action button (open / apply).
//
// Speedups can't be applied from here — they need the order context
// (Castle / Army / Research / Hospital screen). Inventory tile shows
// "Применяется на странице..." for them. Bundles / Shields / Buffs /
// Crystals / Shards apply in-place.
export function InventoryView({ enabled }: Props) {
  const castle = useCastle();
  const openBundle = useOpenBundle();
  const applyShield = useApplyShield();
  const applyBuff = useApplyBuff();
  const [filter, setFilter] = useState<InventoryGroup>("all");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!enabled) return null;
  if (castle.isPending) return <Loading message="Открываю инвентарь..." />;
  if (castle.isError) return <ErrorView message="Не получилось загрузить инвентарь." />;

  const inv = castle.data?.state.inventory ?? {};

  // Show only items the player actually owns. Empty categories collapse
  // to an inline "ничего нет в этой категории" placeholder rather than
  // a grid of dimmed icons — keeps the screen calm and focused.
  const visibleItems = useMemo(() => {
    return InventoryItems.filter((it) => {
      const count = inv[it.id] ?? 0;
      if (count <= 0) return false;
      if (filter !== "all" && groupOf(it) !== filter) return false;
      return true;
    });
  }, [filter, inv]);

  const ownedCount = Object.values(inv).filter((n) => n > 0).length;

  const selectedItem = selectedId ? InventoryItemMap[selectedId] : null;
  const selectedCount = selectedItem ? (inv[selectedItem.id] ?? 0) : 0;

  const onActivate = async (item: InventoryItemDef) => {
    hapticImpact("medium");
    setPendingId(item.id);
    try {
      if (item.kind === "bundle" || item.kind === "heroCrystal" || item.kind === "heroShard") {
        await openBundle.mutateAsync({ itemId: item.id, count: 1 });
        hapticNotification("success");
      } else if (item.kind === "shield") {
        await applyShield.mutateAsync(item.id);
        hapticNotification("success");
      } else if (item.kind === "buff") {
        await applyBuff.mutateAsync(item.id);
        hapticNotification("success");
      }
      setSelectedId(null);
    } catch {
      hapticNotification("error");
      await showAlert("Не получилось применить предмет.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="space-y-3 px-3 pb-3">
      {ownedCount === 0 && (
        <div className="rounded-xl bg-tg-secondaryBg p-3 text-center text-sm text-tg-hint">
          В инвентаре пусто. Предметы приходят из квестов, сундуков за
          ежедневные задания, побед в логовах монстров и из магазина.
        </div>
      )}

      <div className="sticky top-0 z-10 -mx-3 flex gap-1 overflow-x-auto bg-tg-bg/95 px-3 py-2 backdrop-blur-sm">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${
              filter === f.id
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-hint"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visibleItems.length === 0 && ownedCount > 0 && (
        <div className="rounded-xl bg-tg-secondaryBg/60 p-3 text-center text-sm text-tg-hint">
          В этой категории ничего нет.
        </div>
      )}

      <div className="grid grid-cols-4 gap-1.5">
        {visibleItems.map((it) => {
          const count = inv[it.id] ?? 0;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => setSelectedId(it.id)}
              className="relative flex aspect-square flex-col items-center justify-center gap-0.5 rounded-xl bg-tg-secondaryBg p-1 transition-all active:scale-95"
            >
              <ItemIcon item={it} />
              <div className="line-clamp-1 text-[9px] leading-tight text-center text-tg-hint">
                {shortLabel(it)}
              </div>
              <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-tg-button px-1 text-[10px] font-bold tabular-nums leading-[16px] text-tg-buttonText">
                {formatNumber(count)}
              </span>
            </button>
          );
        })}
      </div>

      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          count={selectedCount}
          pending={pendingId === selectedItem.id}
          onClose={() => setSelectedId(null)}
          onActivate={() => onActivate(selectedItem)}
        />
      )}
    </div>
  );
}

function ItemIcon({ item, size = 24 }: { item: InventoryItemDef; size?: number }) {
  const className = `${tintFor(item)}`;
  switch (item.kind) {
    case "speedup":
      return <GiAlarmClock size={size} className={className} />;
    case "bundle":
      return <GiTreasureMap size={size} className={className} />;
    case "shield":
      return <GiShield size={size} className={className} />;
    case "buff":
      return <GiFairyWand size={size} className={className} />;
    case "heroCrystal":
      return <GiCrystalCluster size={size} className={className} />;
    case "heroShard":
      return <GiCrystalShine size={size} className={className} />;
  }
}

// Color tint per group / sub-type so the grid reads at a glance without
// having to inspect tile titles.
function tintFor(item: InventoryItemDef): string {
  if (item.kind === "speedup") {
    if (item.appliesToOrderKind === undefined) return "text-tg-accent";       // universal
    if (item.appliesToOrderKind === 0) return "text-amber-400";                // build
    if (item.appliesToOrderKind === 1) return "text-red-400";                  // training
    if (item.appliesToOrderKind === 2) return "text-emerald-400";              // heal
    if (item.appliesToOrderKind === 4) return "text-sky-400";                  // research
    return "text-tg-hint";
  }
  if (item.kind === "bundle") {
    if (item.id.includes("gold")) return "text-yellow-400";
    if (item.id.includes("wood")) return "text-amber-600";
    if (item.id.includes("stone")) return "text-slate-300";
    if (item.id.includes("mana")) return "text-purple-400";
    return "text-tg-accent";
  }
  if (item.kind === "shield") return "text-sky-400";
  if (item.kind === "buff") {
    if (item.buffKind === "attack") return "text-red-400";
    if (item.buffKind === "defense") return "text-blue-400";
    if (item.buffKind === "production") return "text-green-400";
    if (item.buffKind === "training") return "text-orange-400";
    return "text-tg-accent";
  }
  if (item.kind === "heroCrystal") {
    if (item.heroCrystalTier === 1) return "text-blue-300";
    if (item.heroCrystalTier === 2) return "text-purple-300";
    if (item.heroCrystalTier === 3) return "text-yellow-300";
    return "text-tg-accent";
  }
  if (item.kind === "heroShard") {
    if (item.heroShardRarity === 0) return "text-slate-300";
    if (item.heroShardRarity === 1) return "text-blue-300";
    if (item.heroShardRarity === 2) return "text-purple-300";
    if (item.heroShardRarity === 3) return "text-yellow-300";
    return "text-tg-accent";
  }
  return "text-tg-accent";
}

function shortLabel(item: InventoryItemDef): string {
  if (item.kind === "speedup") return speedupShortLabel(item.speedupSeconds ?? 0);
  if (item.kind === "bundle") return bundleShortLabel(item.id);
  if (item.kind === "shield") return shieldShortLabel(item.shieldDurationSeconds ?? 0);
  if (item.kind === "buff") {
    if (item.buffKind === "attack") return "Атака";
    if (item.buffKind === "defense") return "Защита";
    if (item.buffKind === "production") return "Жатва";
    if (item.buffKind === "training") return "Трен.";
    return item.buffKind ?? "Бафф";
  }
  if (item.kind === "heroCrystal") {
    if (item.heroCrystalTier === 1) return "Тусклый";
    if (item.heroCrystalTier === 2) return "Сияющий";
    if (item.heroCrystalTier === 3) return "Древний";
    return "Кристалл";
  }
  if (item.kind === "heroShard") {
    if (item.heroShardRarity === 0) return "Common";
    if (item.heroShardRarity === 1) return "Rare";
    if (item.heroShardRarity === 2) return "Epic";
    if (item.heroShardRarity === 3) return "Legend.";
    return "Осколок";
  }
  return "";
}

function speedupShortLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}с`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}м`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}ч`;
  return `${Math.round(seconds / 86400)}д`;
}

function shieldShortLabel(seconds: number): string {
  if (seconds <= 8 * 3600) return "8ч щит";
  if (seconds <= 24 * 3600) return "24ч щит";
  return "3д щит";
}

function bundleShortLabel(itemId: string): string {
  const sizeShort =
    itemId.endsWith("-s") ? "S" :
    itemId.endsWith("-m") ? "M" :
    itemId.endsWith("-l") ? "L" : "";
  if (itemId.includes("gold")) return `Зол. ${sizeShort}`;
  if (itemId.includes("wood")) return `Дер. ${sizeShort}`;
  if (itemId.includes("stone")) return `Кам. ${sizeShort}`;
  if (itemId.includes("mana")) return `Мана ${sizeShort}`;
  if (itemId.includes("mixed")) return `Сборн. ${sizeShort}`;
  return sizeShort;
}

function ItemDetailModal({
  item,
  count,
  pending,
  onClose,
  onActivate,
}: {
  item: InventoryItemDef;
  count: number;
  pending: boolean;
  onClose: () => void;
  onActivate: () => void;
}) {
  // Speedups need an order context — we tell the player where to go.
  const isSpeedup = item.kind === "speedup";
  const action = !isSpeedup
    ? (item.kind === "shield" ? "Активировать"
      : item.kind === "buff" ? "Применить"
      : "Открыть")
    : null;

  const speedupHint = isSpeedup
    ? item.appliesToOrderKind === undefined
      ? "Применяется на странице Замок / Армия / Исследования — тап по активному заказу."
      : item.appliesToOrderKind === 0
        ? "Применяется на странице Замок — тап по активной стройке."
        : item.appliesToOrderKind === 1
          ? "Применяется на странице Армия — тап по активной тренировке."
          : item.appliesToOrderKind === 2
            ? "Применяется на странице Армия — тап по активной партии лечения."
            : item.appliesToOrderKind === 4
              ? "Применяется на странице Исследования."
              : null
    : null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="animate-pop-in w-full max-w-md rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-tg-secondaryBg">
            <ItemIcon item={item} size={32} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight">{item.title}</h3>
            <div className="mt-0.5 text-xs text-tg-hint">
              В наличии: <span className="font-semibold tabular-nums">{formatNumber(count)}</span>
            </div>
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-tg-hint">{item.description}</p>
        {speedupHint && (
          <p className="mt-2 rounded-lg bg-tg-secondaryBg/60 p-2 text-xs text-tg-hint">
            {speedupHint}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl bg-tg-secondaryBg px-3 py-2 text-sm font-medium text-tg-hint"
          >
            Закрыть
          </button>
          {action && (
            <button
              type="button"
              onClick={onActivate}
              disabled={count === 0 || pending}
              className="flex-1 rounded-xl bg-tg-button px-3 py-2 text-sm font-semibold text-tg-buttonText disabled:opacity-50"
            >
              {pending ? "..." : action}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
